// ---------------------------------------------------------------------------
// Prezio Onboard Widget — Root Component
// ---------------------------------------------------------------------------
// Pure composition: wires providers around the shell layout.
// No business logic lives here — it just connects the pieces.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';
import type { ApiClient } from '@/api/client';
import type { ThemeConfig } from '@/types/config';
import type { CompletionResult, StepChangeEvent, ErrorEvent } from '@/types/events';
import type { CountryPreset } from '@/utils/configSanitizer';
import { DEFAULT_THEME } from '@/utils/constants';
import { ThemeProvider } from '@/context/ThemeContext';
import { SessionProvider } from '@/context/SessionContext';
import { WidgetConfigProvider } from '@/context/WidgetConfigContext';
import { OnboardShell, ShellContent } from '@/components/shell/OnboardShell';
import { ProgressBar } from '@/components/shell/ProgressBar';
import { NavHeader } from '@/components/shell/NavHeader';
import { LogoBar } from '@/components/shell/LogoBar';
import { StepRouter } from '@/components/steps/StepRouter';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PrezioOnboardProps {
  apiClient: ApiClient;
  sessionId: string;
  /** The DOM element to apply CSS custom properties on (resolved in init). */
  rootElement: HTMLElement;
  theme?: ThemeConfig;
  /** Whether to display step subtitles. Defaults to `true`. */
  showStepSubtitles?: boolean;
  /** Show geocoding details on the address confirmation screen. Defaults to `false`. */
  demoMode?: boolean;
  /** Per-country predefined step values. */
  countryPresets?: Record<string, CountryPreset>;
  /** URL shown in the sandbox coverage nudge banner. */
  upgradeUrl?: string | null;
  onComplete?: (result: CompletionResult) => void;
  onCancel?: () => void;
  onError?: (error: ErrorEvent) => void;
  onStepChange?: (event: StepChangeEvent) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PrezioOnboard({
  apiClient,
  sessionId,
  rootElement,
  theme = DEFAULT_THEME,
  showStepSubtitles = true,
  demoMode = false,
  countryPresets,
  upgradeUrl,
  onComplete,
  onCancel,
  onError,
  onStepChange,
}: PrezioOnboardProps): ReactNode {
  return (
    <ThemeProvider theme={theme} rootElement={rootElement}>
      <SessionProvider
        apiClient={apiClient}
        sessionId={sessionId}
        onComplete={onComplete}
        onCancel={onCancel}
        onError={onError}
        onStepChange={onStepChange}
      >
          <WidgetConfigProvider showStepSubtitles={showStepSubtitles} demoMode={demoMode} countryPresets={countryPresets} upgradeUrl={upgradeUrl}>
          <OnboardShell>
            <NavHeader onClose={onCancel} />
            <ProgressBar />
            <LogoBar />
            <ShellContent>
              <StepRouter />
            </ShellContent>
          </OnboardShell>
        </WidgetConfigProvider>
      </SessionProvider>
    </ThemeProvider>
  );
}
